import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Controlled as CodeMirror } from 'react-codemirror2';
import clsx from 'clsx';
import Moment from 'moment';
import { toast } from 'react-toastify';
import {
    sortBy as _sortBy,
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';

import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';

import PolicyService from '../../../data/policy-service';
import SuricataService from '../../../data/suricata-service';
import DataContext from '../../../data/data-context';

import Searchbar from '../../../components/searchbar';
import ConfirmationDialog from '../../../components/confirmationdlg';

import useWindowSize from '../../../libraries/usewindowsize';

const drawerWidth = '300px';
const drawerWidthCollapsed = '60px !important';

const useStyles = makeStyles(theme => ({
    content: {
        marginTop: '-20px',
        marginLeft: '-8px',
    },
    justifySpace: {
        justifyContent: 'space-between',
    },
    formControl: {
        margin: theme.spacing(1),
        width: '100%',
    },
    textfield: {
        width: '50%',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(1),
    },
    drawer: {
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        backgroundColor: theme.palette.nav.paper,
        color: theme.palette.nav.text,
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
        transition: 'width 500ms',
    },
    drawerCollapsed: {
        width: drawerWidthCollapsed,
        transition: 'width 500ms',
    },
    drawerPaper: {
        width: drawerWidth,
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
        marginLeft: '60px',
        background: theme.palette.nav.paper,
        color: theme.palette.nav.text,
        transition: 'margin-left 500ms, width 500ms',
    },
    drawerPaperExtended: {
        marginLeft: '300px',
        transition: 'margin-left 500ms, width 500ms',
    },
    controlBar: {
        display: 'none',
    },
    header: {
        padding: '10px 16px',
        '& .MuiCardHeader-title': {
            fontSize: '16px',
        },
        '& .MuiCardHeader-subheader': {
            fontSize: '14px',
        }
    },
    cardContent: {
        minHeight: '40px',
        padding: '0px 16px',
        // fontSize: '14px',
    },
    gridOverflow: {
        maxHeight: window.innerHeight - 200,
        overflowY: 'scroll',
    },
    editableBox: {
        marginLeft: theme.spacing(3),
        marginRight: theme.spacing(3),
        padding: theme.spacing(1),
        background: theme.palette.common.black,
        border: `1px solid ${theme.palette.warning.main}`,
    },
    hideFileInput: {
        display: 'none',
    },
    listItem: {
        borderBottom: `1px solid ${theme.palette.common.white}`,
        '& p': {
            whiteSpace: 'pre-wrap',
        }
    },
    codeContainer: {
        // marginBottom: '-24px',
        '& .react-codemirror2': {
            height: '100%',
        },
        '& .CodeMirror': {
            height: '100%',
        }
    },
    editorButton: {
        marginTop: '8px',
        marginLeft: '8px'
    },
    selectedListItem: {
        background: 'rgba(100, 100, 100, 0.25)',
    },
    staticList: {
        // position: 'static',
        // top: 0,
    },
    scrollList: {
        height: '100%',
        overflowY: 'scroll',
    },
    ruleDetails: {
        color: theme.palette.common.white,
        // display: 'flex',
        // justifyContent: 'space-between',
        marginRight: theme.spacing(1),
    },
    confirmationRuleText: {
        color: theme.palette.secondary.main,
    },
    listItemTooltip: {
        cursor: 'pointer',
        padding: '5px 18px',
    },
    tooltipFont: {
        color: theme.palette.common.white,
    },
}));

const emptyRule = {
    id: '',
    action: 'alert',
    name: 'Custom Rule',
    header: 'ip $EXTERNAL_NET any -> $HOME_NET any',
    options: 'msg:"Custom Rule";',
};

const SORT_TYPES = [
    { value: 'name', name: 'Name' },
];

const SuricataEditor = () => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const { systemSettings, sidenav, setSidenav } = useContext(DataContext);
    const [sortAnchor, setSortAnchor] = useState(null);
    const [sort, setSort] = useState('name+desc');
    const [policyType, setPolicyType] = useState('suricata_rules');
    const [rules, setRules] = useState([]);
    const [selectedRule, _setSelectedRule] = useState({ ...emptyRule });
    const [init, setInit] = useState(false);
    const [edit, setEdit] = useState(false);
    const [original, _setOriginal] = useState(_cloneDeep(emptyRule));
    const [changes, setChanges] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [ruleQuery, setRuleQuery] = useState({
        page: 0,
        limit: 50,
        search: '',
        sort: 0,
        group: 'custom_rules',
        mod: {
            stack: []
        }
    });
    const [isHeader, setIsHeader] = useState(false);
    const { height } = useWindowSize();
    const [headerEditor, setHeaderEditor] = useState(null);
    const [optionsEditor, setOptionsEditor] = useState(null);
    const [deleteWait, setDeleteWait] = useState(false);
    const [managedStatus, setManagedStatus] = useState(false);
    const [skipEffects, setSkipEffects] = useState(false);
    const [prevPath, setPrevPath] = useState('');

    useEffect(() => {
        if (location.state && location.state.prevPath) {
            setPrevPath(location.state.prevPath);
        }
    }, [location.state]);

    useEffect(() => {
        if (systemSettings && systemSettings.ui) {
            setIsHeader(Boolean(systemSettings.ui.messages.header));
        }
    }, [systemSettings]);

    useEffect(() => {
        if (location.pathname.match('bt_rules')) {
            setPolicyType('bt_suricata_manual');
        } else {
            setPolicyType('suricata_rules');
        }

        getManagedStatus();

        setSidenav({ sidebar: 'open' });

        return () => {
            setSidenav({ sidebar: undefined });
        };
    }, []);

    useEffect(() => {
        setSidenav({ sidebar: managedStatus ? 'closed' : 'open' });
    }, [managedStatus]);

    useEffect(() => {
        if (policyType) {
            getRules();
        }
    }, [policyType, sort]);

    useEffect(() => {
        setChanges(!_isEqual(selectedRule, original));
    }, [selectedRule]);

    useEffect(() => {
        let interval;
        if (headerEditor) {
            interval = setInterval(() => {
                headerEditor.refresh();
            }, 500);
        }

        return () => {
            clearInterval(interval);
        };
    }, [headerEditor]);

    useEffect(() => {
        let interval;
        if (optionsEditor) {
            interval = setInterval(() => {
                optionsEditor.refresh();
            }, 500);
        }

        return () => {
            clearInterval(interval);
        };
    }, [optionsEditor]);

    useEffect(() => {
        if (params.id && params.policy) {
            getRules();
        }
    }, [params.id, params.policy]);

    useEffect(() => {
        if (policyType && !skipEffects) {
            getRules();
        }
    }, [policyType, sort]);

    const getManagedStatus = () => {
        SuricataService.isManaged().then(res => {
            setManagedStatus(res.managed);
        }, err => {
            console.error('Unable to get managed status ::: ', err);
        });
    };

    // de-couple the selected rule from the list, because the list will only return first 50 rules
    const getRules = useCallback((search) => {
        PolicyService.getPolicies(policyType).then(p => {
            SuricataService.listPolicyRules((p.length ? p[0].name : 'default'), policyType, { ...ruleQuery, search }).then(res => {
                if (!res.rules) {
                    res.rules = [];
                }
                res.rules = _sortBy(res.rules, [sort.split('+')[0]]);
                if (sort.split('+')[1] === 'asc') {
                    res.rules.reverse();
                }

                setRules(res.rules);
            });
        });
    }, [policyType, sort, ruleQuery]);

    useEffect(() => {
        if (params.id && params.policy && !skipEffects) {
            SuricataService.getPolicyRuleDetails(params.policy, params.id).then(res => {
                setOriginal(res);
                setSelectedRule(res);
                setEdit(true);
                setInit(true);
            }).catch(err => {
                console.error(err);
            });
        }
    }, [params.id, params.policy]);

    const setOriginal = useCallback((data) => {
        data = data || { ...emptyRule };

        if (data && data.id) {
            data.options = data.options.replace(`sid:${data.id};`, '')
                .replace(`rev:${data.ver};`, '');
        }

        _setOriginal(data);
    }, []);

    const setSelectedRule = useCallback((data) => {
        data = data || { ...emptyRule, ...data };

        if (data && data.id) {
            data.options = data.options.replace(`sid:${data.id};`, '')
                .replace(`rev:${data.ver};`, '');

            if (params.id !== data.id || params.policy !== (params.policy || 'default')) {
                setSkipEffects(true);
                navigate('/policies/suricata/rules/editor/' + data.id + `/${params.policy || 'default'}`, {
                    state: { prevPath }
                });
                // Reset the skip flag after navigation completes
                setTimeout(() => {
                    setSkipEffects(false);
                }, 50);
            }
        }

        _setSelectedRule(data);
    }, [navigate, params.id, params.policy]);

    const handleUpdateAction = (action) => {
        const s = { ...selectedRule };
        s.action = action;
        setSelectedRule(s);
    };

    // const handleUpdateMessage = (editor, data, value) => {
    //     setSelectedRule({
    //         ...selectedRule,
    //         name: value,
    //     });
    // };

    const handleUpdateHeader = (editor, data, value) => {
        setSelectedRule({
            ...selectedRule,
            header: value,
        });
    };

    const handleUpdateOptions = (editor, data, value) => {
        setSelectedRule({
            ...selectedRule,
            options: value,
        });
    };

    const handleDeleteScript = (e) => {
        setDeleteWait(true);
        SuricataService.deleteRule(selectedRule.id).then(res => {
            getRules();
            handleClear();
            setDeleteWait(false);
            toast.success('Rule deleted successfully');
            navigate('/policies/suricata/rules/editor', {
                state: { prevPath }
            });
        }, err => {
            toast.success('Unable to delete rule...');
            console.error(err);
        });

        handleOpenDelete();
    };

    const handleClear = () => {
        setEdit(false);
        setSelectedRule();
        setOriginal(_cloneDeep(emptyRule));
        navigate('/policies/suricata/rules/editor', {
            state: { prevPath }
        });
    };

    const handleOpenDelete = (i) => {
        setDeleteWait(false);
        if (typeof i === 'number') {
            handleSelectCard(i);
            setDeleteOpen(!deleteOpen);
        } else {
            setDeleteOpen(!deleteOpen);
            setSelectedRule({ ...emptyRule });
            setEdit(false);
        }
    };

    function debounce (func, delay) {
        let timer;
        return function (...args) {
            clearTimeout(timer);
            timer = setTimeout(() => func.apply(this, args), delay);
        };
    }

    const handleCreate = () => {
        if (selectedRule && !selectedRule.header) {
            toast.error('Header cannot be empty');
            return;
        } else if (selectedRule && !selectedRule.options) {
            toast.error('Options cannot be empty');
            return;
        }
        let name = selectedRule.name;
        const m = selectedRule.options.match(/msg:(\s*|)"[\w\W\s]+?";\s*/);

        if (m && m.length) {
            if (m[0].length) {
                name = m[0].replace(/msg:|"|;/gim, '').trim();
            }
        } else {
            toast.error('Invalid keyword msg');
            return;
        }
        if (edit) {
            SuricataService.updateCustomRule(selectedRule.id, {
                action: selectedRule.action,
                header: selectedRule.header,
                message: name,
                options: selectedRule.options.replace(`msg:"${name}";`, ''),
            }).then(res => {
                getRules();
                toast.success('Rule updated successfully');
                setChanges(false);
            }, err => {
                toast.error(`${err.message}: ${err.error}`);
            });
        } else {
            SuricataService.createCustomRule({
                action: selectedRule.action,
                header: selectedRule.header,
                message: name,
                options: selectedRule.options.replace(`msg:"${name}";`, ''),

            }).then(res => {
                getRules();
                toast.success('Rule created successfully');
                setChanges(false);
                handleClear();
            }, err => {
                toast.error(`${err.message}: ${err.error}`);
            });
        }
    };

    const handleCancel = () => {
        handleClear();
    };

    const handleUndoChanges = () => {
        setSelectedRule(_cloneDeep(original));
    };

    const handleSelectCard = (i) => {
        setEdit(true);
        setOriginal(_cloneDeep(rules[i]));
        setSelectedRule(rules[i]);
    };

    const parseRuleMessage = (rule) => {
        const options = rule?.options || '';
        const msg = options.match(/msg:(\s*|)"[\w\W\s]+?";\s*/);
        if (msg && msg.length && msg[0].length) {
            return msg[0].replace(/msg:|"|;/gim, '').trim();
        }
        return rule?.name || 'Custom Rule';
    };

    const normalizeRuleOptions = (rule, message) => {
        return (rule?.options || '')
            .replace(`msg:"${message}";`, '')
            .replace(/\bsid\s*:\s*\d+\s*;?/gi, '')
            .replace(/\brev\s*:\s*\d+\s*;?/gi, '')
            .trim();
    };

    const handleCloneRule = (rule) => {
        const message = parseRuleMessage(rule);
        const existingMessages = rules.map(r => parseRuleMessage(r));
        const cloneSuffix = /^(.*?)-clone\s*\((\d+)\)\s*$/;
        const baseMatch = message.match(cloneSuffix);
        const baseMessage = baseMatch ? baseMatch[1].trim() : message;

        const escapedBase = baseMessage.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const clonePattern = new RegExp(`^${escapedBase}-clone\\s*\\((\\d+)\\)\\s*$`);

        let cloneIndex = 0;
        existingMessages.forEach(existingMessage => {
            const match = existingMessage.match(clonePattern);
            if (match) {
                cloneIndex = Math.max(cloneIndex, Number(match[1]));
            }
        });
        cloneIndex += 1;

        const cloneMessage = `${baseMessage}-clone (${cloneIndex})`;

        const body = {
            action: rule.action,
            header: rule.header,
            message: cloneMessage,
            options: normalizeRuleOptions(rule, message),
        };

        SuricataService.createCustomRule(body).then(() => {
            toast.success('Rule cloned successfully');
            getRules();
        }, err => {
            toast.error(`${err.message}: ${err.error}`);
        });
    };

    const handleSearchScripts = ({ search }) => {
        getRules(search);
        if (!search) {
            setSelectedRule({ ...emptyRule });
        }
    };

    const handleGoBack = useCallback(() => {
        navigate(-1); // Go back to previous entry in history
    }, [navigate]);

    // const handleCloseSort = () => {
    //     setSortAnchor(null);
    // };

    const handleUpdateSort = (v) => {
        if (sort.split('+')[1] === 'desc') {
            setSort('name+asc');
            return;
        }
        setSort('name+desc');

        // setSortAnchor(null);
        // if(v === sort.split('+')[0]) {
        //     if(sort.split('+')[1] === 'desc') {
        //         setSort(v+'+asc');
        //         return;
        //     }
        // }
        // setSort(v+'+desc')
    };

    const codemirrorOptions = {
        lineNumbers: true,
        theme: theme.codeMirror.theme,
        mode: 'suricata',
        lineWrapping: true,
        gutters: ['padding-bottom: 20px']
    };

    return (
        <React.Fragment>
            {managedStatus
                ? <Box mt={2}>
                    <Typography variant="h6">Rules can only be edited on the MC2.</Typography>
                </Box>
                : <main className={classes.content}>
                    <Drawer
                        open={true}
                        className={classes.drawer}
                        variant="persistent"
                        classes={{
                            paper: clsx({
                                [classes.drawerPaper]: true,
                                [classes.drawerPaperExtended]: !sidenav.collapsed
                            }),
                        }}
                    >
                        <Box style={{ marginTop: (isHeader ? 98 : 72) }}></Box>
                        <List className={classes.staticList}>
                            <ListItem>
                                <Searchbar immediate onSearch={handleSearchScripts} searchservice={SuricataService} />
                            </ListItem>
                            <ListItem>
                                <Button variant="contained" color="primary" aria-label="name sort" onClick={handleUpdateSort}>
                                    Name {sort.split('+')[1] === 'desc' ? <ExpandMore /> : <ExpandLess />}
                                </Button>
                                {/*  Leaving this incase additional sorting is wanted later
                            <Button variant="contained" color="primary" onClick={(e) => {setSortAnchor(e.currentTarget)}}>
                                Name {sort.split('+')[1] === 'desc' ? <ExpandMore /> : <ExpandLess />}
                            </Button>
                            <Menu
                                anchorEl={sortAnchor}
                                open={Boolean(sortAnchor)}
                                onClose={handleCloseSort}
                            >
                                {SORT_TYPES.map((v, i) => {
                                    return (
                                        <MenuItem
                                            key={"sort-menu-item-"+i}
                                            className={classes.menuItem}
                                            onClick={() => {handleUpdateSort(v.value)}}
                                        >
                                            <Box display="flex" justifyContent="space-between" width="100%">
                                                {v.name}
                                                {sort.split('+')[0] === v.value ? (sort.split('+')[1] === 'desc' ? <ExpandMore /> : <ExpandLess />) : null}
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                            </Menu> */}
                            </ListItem>
                        </List>
                        <List className={classes.scrollList}>
                            {rules.map((v, i) => {
                                return (
                                    <Tooltip title={v.name} key={'suricata-rule-list-item-' + i} className={classes.listItemTooltip} classes={{ tooltip: classes.tooltipFont }}>
                                        <ListItem className={clsx({
                                            [classes.listItem]: true,
                                            [classes.selectedListItem]: selectedRule.id === v.id,
                                        })} onClick={() => { handleSelectCard(i) }}>
                                            <ListItemText
                                                disableTypography
                                                primary={<Typography variant="body2" component="p">{v.name}</Typography>}
                                                secondary={
                                                    <Box className={classes.ruleDetails}>
                                                        <Box display="flex" justifyContent="space-between">
                                                            <Box>Group: custom_rules</Box>
                                                            <Box mr={1}>ver: {v.ver}</Box>
                                                        </Box>
                                                        <Box>{Moment(v.inserted).format(theme.date.long)}</Box>
                                                    </Box>
                                                }
                                            />
                                            <Box display="block">
                                                <IconButton style={{ display: "block" }} size="small" variant="contained" color="primary" aria-label="clone" onClick={() => { handleCloneRule(v) }}>
                                                    <FileCopyIcon />
                                                </IconButton>
                                                <IconButton style={{ display: "block" }} size="small" variant="contained" color="secondary" aria-label="delete" onClick={() => { handleOpenDelete(i) }}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        </ListItem>
                                    </Tooltip>
                                );
                            })}
                            <ListItem style={{ height: '50px' }}></ListItem>
                        </List>
                    </Drawer>
                    <Box display="flex" justifyContent="space-between">
                        <Box></Box>
                        <Box display="flex">
                            <a target="_blank" aria-label="info on rules" href="https://suricata.readthedocs.io/en/latest/rules/index.html" rel="noreferrer noopener">
                                <IconButton aria-label="info on rules" size="large"><InfoIcon /></IconButton>
                            </a>
                            <Box ml={1} mt={1}><Button
                                className={classes.buttonRight}
                                variant="contained"
                                color="primary"
                                aria-label="clear"
                                onClick={handleCancel}
                            >
                                New Rule
                            </Button></Box>
                            <Box ml={1} mt={1}><Button
                                className={classes.buttonRight}
                                variant="contained"
                                color="primary"
                                aria-label={edit ? 'update' : 'create'}
                                onClick={debounce(handleCreate, 1000)}
                                disabled={!changes}
                            >
                                {edit ? 'Update' : 'Create'}
                            </Button></Box>
                            <Box ml={1} mt={1}><Button
                                className={classes.buttonRight}
                                variant="contained"
                                color="secondary"
                                aria-label="clear"
                                onClick={handleUndoChanges}
                                disabled={!changes}
                            >
                                Cancel
                            </Button></Box>
                            <Box ml={2} mt={1}><Button variant="contained" aria-label="go back" onClick={handleGoBack}>Back</Button></Box>
                        </Box>
                    </Box>
                    <Box mt={2}>
                        <Typography>Actions</Typography>
                        <ButtonGroup>
                            <Button
                                color="secondary"
                                variant={selectedRule.action === 'pass' ? 'contained' : 'outlined'}
                                aria-label="pass"
                                onClick={() => { handleUpdateAction('pass') }}
                            >Pass</Button>
                            <Button
                                color="secondary"
                                variant={selectedRule.action === 'drop' ? 'contained' : 'outlined'}
                                aria-label="drop"
                                onClick={() => { handleUpdateAction('drop') }}
                            >Drop</Button>
                            <Button
                                color="secondary"
                                variant={selectedRule.action === 'alert' ? 'contained' : 'outlined'}
                                aria-label="alert"
                                onClick={() => { handleUpdateAction('alert') }}
                            >Alert</Button>
                        </ButtonGroup>
                    </Box>
                    {/* <Box mt={2} className={classes.codeContainer} width="100%" height={50}>
                    <Box><Typography>Message</Typography></Box>
                    <CodeMirror
                        value={selectedRule.name}
                        options={codemirrorOptions}
                        onBeforeChange={handleUpdateMessage}
                        onChange={(e, d, v) => {setSelectedRule({...selectedRule, message: v})}}
                        lineWrapping={true}
                    />
                </Box> */}
                    <Box mt={4} className={classes.codeContainer} width="100%" height={(height / 2) - 250}>
                        <Box><Typography>Header</Typography></Box>
                        <CodeMirror
                            editorDidMount={e => { setHeaderEditor(e) }}
                            value={selectedRule.header}
                            options={codemirrorOptions}
                            onBeforeChange={handleUpdateHeader}
                            onChange={(e, d, v) => { setSelectedRule({ ...selectedRule, header: v }) }}
                        />
                    </Box>
                    <Box mt={4} className={classes.codeContainer} width="100%" height={(height / 2) - 150}>
                        <Box><Typography>Options</Typography></Box>
                        <CodeMirror
                            editorDidMount={e => { setOptionsEditor(e) }}
                            value={selectedRule.options}
                            options={codemirrorOptions}
                            onBeforeChange={handleUpdateOptions}
                            onChange={(e, d, v) => { setSelectedRule({ ...selectedRule, options: v }) }}
                        />
                    </Box>
                    <ConfirmationDialog
                        onClose={() => {
                            const i = rules.findIndex(v => (v.name === selectedRule.name && v.inserted === selectedRule.inserted));
                            if (i !== -1) {
                                handleOpenDelete(i);
                            } else {
                                handleOpenDelete();
                            }
                        }}
                        onOK={handleDeleteScript}
                        open={deleteOpen}
                        message={(() => {
                            return (
                                <Box>
                                    Are you sure you want to delete the rule
                                    <Box my={2} className={classes.confirmationRuleText}>{selectedRule.name}</Box>
                                    from the custom_rules group?
                                </Box>
                            );
                        })()}
                        title="Delete Rule"
                        cancelLabel="Cancel"
                        okLabel="Yes"
                        disableOK={deleteWait}
                    />
                </main>
            }
        </React.Fragment>
    );
};

export default SuricataEditor;
